// Staff Negotiation Queue — /operations/negotiations
import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Handshake,
  Search,
  Filter,
  ArrowUpRight,
  ArrowRight,
  UserCircle2,
  Building2,
  Flame,
  TrendingUp,
  Clock,
  CircleDot,
  Inbox,
  MessageSquare,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { AuthGate } from "@/components/auth-gate";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useRecoveryFacts } from "@/hooks/use-recovery-facts";
import {
  NOT_AVAILABLE,
  resolveNegotiationFinancial,
} from "@/lib/financial/surface-financials";
import type { Negotiation, NegotiationMessage, Review } from "@/lib/db/models";

export const Route = createFileRoute("/operations/negotiations/")({
  head: () => ({
    meta: [
      { title: "ClaimGuard | Negotiation Queue — Operations" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AuthGate allow={["staff", "admin"]}>
      <NegotiationQueuePage />
    </AuthGate>
  ),
});

type Filter =
  | "all"
  | "assigned"
  | "unassigned"
  | "waiting_vendor"
  | "negotiating"
  | "offer_received"
  | "completed"
  | "urgent"
  | "high_value";

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: "all", label: "All" },
  { key: "unassigned", label: "Unassigned" },
  { key: "assigned", label: "Assigned" },
  { key: "waiting_vendor", label: "Waiting Vendor" },
  { key: "negotiating", label: "Negotiating" },
  { key: "offer_received", label: "Offer Received" },
  { key: "completed", label: "Completed" },
  { key: "urgent", label: "Urgent" },
  { key: "high_value", label: "High Value" },
];

interface Row {
  negotiation: Negotiation;
  review: Review | null;
  lastMessage: NegotiationMessage | null;
}

function NegotiationQueuePage() {
  const { user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  const [assigning, setAssigning] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: negs } = await supabase
        .from("negotiations")
        .select("*")
        .order("created_at", { ascending: false });
      const negotiations = (negs ?? []) as Negotiation[];
      const reviewIds = [...new Set(negotiations.map((n) => n.review_id))];
      const negIds = negotiations.map((n) => n.id);

      const reviewMap = new Map<string, Review>();
      const lastMsgMap = new Map<string, NegotiationMessage>();

      if (reviewIds.length) {
        const { data: reviews } = await supabase
          .from("reviews")
          .select("*")
          .in("id", reviewIds);
        for (const r of (reviews ?? []) as Review[]) reviewMap.set(r.id, r);
      }
      if (negIds.length) {
        const { data: msgs } = await supabase
          .from("negotiation_messages")
          .select("*")
          .in("negotiation_id", negIds)
          .order("created_at", { ascending: false });
        for (const m of (msgs ?? []) as NegotiationMessage[]) {
          if (!lastMsgMap.has(m.negotiation_id)) lastMsgMap.set(m.negotiation_id, m);
        }
      }
      if (cancelled) return;
      setRows(
        negotiations.map((n) => ({
          negotiation: n,
          review: reviewMap.get(n.review_id) ?? null,
          lastMessage: lastMsgMap.get(n.id) ?? null,
        })),
      );
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter(({ negotiation: n, review: r }) => {
      if (filter === "unassigned" && n.assigned_specialist_id) return false;
      if (filter === "assigned" && !n.assigned_specialist_id) return false;
      if (
        filter === "waiting_vendor" &&
        !["vendor_contacted", "waiting_response", "waiting_vendor"].includes(n.status)
      )
        return false;
      if (filter === "negotiating" && !["negotiating", "in_progress"].includes(n.status))
        return false;
      if (filter === "offer_received" && n.status !== "offer_received") return false;
      if (filter === "completed" && !["completed", "accepted"].includes(n.status)) return false;
      if (filter === "urgent" && n.priority !== "urgent") return false;
      if (filter === "high_value" && (n.original_amount ?? 0) < 5000) return false;
      if (s) {
        const hay = [n.negotiation_number, r?.file_number, r?.vendor, n.status]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [rows, filter, q]);

  /* SAB-001: canonical recovery per linked review — never an estimate. */
  const linkedReviewIds = useMemo(
    () => rows.map((r) => r.review?.id ?? null).filter((v): v is string => !!v),
    [rows],
  );
  const { facts: recoveryFacts } = useRecoveryFacts(linkedReviewIds);
  const financialFor = useCallback(
    (row: Row) =>
      resolveNegotiationFinancial({
        originalAmount:
          row.negotiation.original_amount ?? row.review?.total_amount ?? null,
        settlementAmount: row.negotiation.settlement_amount ?? null,
        canonicalRecovery: row.review
          ? (recoveryFacts.get(row.review.id)?.recovery ?? null)
          : null,
      }),
    [recoveryFacts],
  );

  const counts = useMemo(
    () => ({
      total: rows.length,
      unassigned: rows.filter((r) => !r.negotiation.assigned_specialist_id).length,
      urgent: rows.filter((r) => r.negotiation.priority === "urgent").length,
      supportedRecovery: rows.reduce((a, r) => a + (financialFor(r).amount ?? 0), 0),
      recoveryCounted: rows.filter((r) => financialFor(r).amount !== null).length,
    }),
    [rows, financialFor],
  );

  async function handleAssignSelf(negotiationId: string) {
    if (!user) {
      toast.error("Sign-in required to assign.");
      return;
    }
    setAssigning(negotiationId);
    const { error } = await supabase
      .from("negotiations")
      .update({ assigned_specialist_id: user.id, status: "assigned" })
      .eq("id", negotiationId);
    setAssigning(null);
    if (error) {
      toast.error("Assignment failed", { description: error.message });
      return;
    }
    setRows((prev) =>
      prev.map((r) =>
        r.negotiation.id === negotiationId
          ? {
              ...r,
              negotiation: {
                ...r.negotiation,
                assigned_specialist_id: user.id,
                status: "assigned",
              },
            }
          : r,
      ),
    );
    toast.success("Assigned to you");
  }

  return (
    <div className="min-h-dvh bg-secondary/30">
      <SiteHeader />
      <div className="container-page space-y-8 py-10">
        <Breadcrumbs
          items={[
            { label: "Operations", to: "/operations" },
            { label: "Negotiations" },
          ]}
        />

        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-negotiate-soft px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-negotiate">
              <Handshake className="h-3.5 w-3.5" />
              Negotiation Queue
            </div>
            <h1 className="mt-3 font-display text-3xl text-navy md:text-4xl">
              Every active negotiation, in one place.
            </h1>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              Prioritize, assign, and drive vendor negotiations to close.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <StatCard label="Open" value={counts.total} icon={<Inbox className="h-4 w-4" />} />
            <StatCard
              label="Unassigned"
              value={counts.unassigned}
              icon={<UserCircle2 className="h-4 w-4" />}
              accent
            />
            <StatCard
              label="Total Supported Recovery"
              value={
                counts.recoveryCounted > 0
                  ? `$${Math.round(counts.supportedRecovery).toLocaleString()}`
                  : NOT_AVAILABLE
              }
              icon={<TrendingUp className="h-4 w-4" />}
            />
          </div>
        </header>

        <section className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <div className="flex flex-wrap gap-2">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFilter(f.key)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      filter === f.key
                        ? "border-negotiate bg-negotiate text-white"
                        : "border-border bg-card text-navy hover:bg-secondary"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search negotiation #, vendor, customer…"
                className="w-72 rounded-lg border border-border bg-card pl-9 pr-3 py-2 text-sm outline-none focus:border-negotiate"
              />
            </div>
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  <th className="px-2 py-3">Negotiation</th>
                  <th className="px-2 py-3">Customer / Vendor</th>
                  <th className="px-2 py-3 text-right">Recovery / Actual Savings</th>
                  <th className="px-2 py-3">Priority</th>
                  <th className="px-2 py-3">Next Action</th>
                  <th className="px-2 py-3">Specialist</th>
                  <th className="px-2 py-3 text-right">Days Open</th>
                  <th className="px-2 py-3">Latest Activity</th>
                  <th className="px-2 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="py-10 text-center text-muted-foreground">
                      Loading negotiations…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-2 py-16">
                      <EmptyState hasAny={rows.length > 0} />
                    </td>
                  </tr>
                ) : (
                  filtered.map(({ negotiation: n, review: r, lastMessage }) => {
                    const days = daysBetween(n.created_at);
                    const financial = financialFor({
                      negotiation: n,
                      review: r,
                      lastMessage,
                    });
                    const nextAction = computeNextAction(n, lastMessage);
                    return (
                      <tr
                        key={n.id}
                        className="border-b border-border/60 transition hover:bg-secondary/40"
                      >
                        <td className="px-2 py-3">
                          <div className="font-mono text-sm font-bold text-navy">
                            {n.negotiation_number ?? `NEG-${n.id.slice(0, 8)}`}
                          </div>
                          <div className="mt-0.5">
                            <StatusPill status={n.status} />
                          </div>
                        </td>
                        <td className="px-2 py-3">
                          <div className="flex items-center gap-2.5">
                            <Avatar
                              name={r?.file_number ?? n.id}
                              className="bg-negotiate-soft text-negotiate"
                            />
                            <div className="min-w-0">
                              <div className="truncate font-semibold text-navy">
                                {r?.file_number ?? "—"}
                              </div>
                              <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground">
                                <Building2 className="h-3 w-3 shrink-0" />
                                <span className="truncate">{r?.vendor ?? "Unknown vendor"}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-3 text-right">
                          {financial.amount !== null ? (
                            <div className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 font-display text-sm font-bold text-emerald-700">
                              <Sparkles className="h-3 w-3" />
                              {financial.value}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">{NOT_AVAILABLE}</span>
                          )}
                          <div className="mt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                            {financial.kind === "actual_settlement"
                              ? "Actual Negotiated Savings"
                              : "Total Supported Recovery"}
                          </div>
                        </td>
                        <td className="px-2 py-3">
                          <PriorityBadge priority={n.priority} />
                        </td>
                        <td className="px-2 py-3">
                          <NextActionChip action={nextAction} />
                        </td>
                        <td className="px-2 py-3 text-navy">
                          {n.assigned_specialist_id ? (
                            <span className="inline-flex items-center gap-1.5 text-xs">
                              <UserCircle2 className="h-4 w-4 text-negotiate" /> Assigned
                            </span>
                          ) : (
                            <span className="text-xs italic text-muted-foreground">
                              Unassigned
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-3 text-right">
                          <DaysOpenPill days={days} />
                        </td>
                        <td className="px-2 py-3 max-w-[220px]">
                          {lastMessage ? (
                            <div className="flex items-start gap-1.5 text-xs">
                              <MessageSquare className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                              <div className="min-w-0">
                                <div className="truncate text-navy">
                                  {truncate(lastMessage.body, 60)}
                                </div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                  {lastMessage.sender_role} · {relativeShort(lastMessage.created_at)}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs italic text-muted-foreground">
                              No messages yet
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            {!n.assigned_specialist_id && (
                              <button
                                type="button"
                                onClick={() => handleAssignSelf(n.id)}
                                disabled={assigning === n.id}
                                className="inline-flex items-center gap-1 rounded-md border border-negotiate/20 bg-card px-2.5 py-1.5 text-xs font-semibold text-negotiate transition hover:bg-negotiate-soft disabled:opacity-50"
                              >
                                {assigning === n.id ? "…" : "Assign me"}
                              </button>
                            )}
                            <Link
                              to="/operations/negotiations/$id"
                              params={{ id: n.id }}
                              aria-label={`Open negotiation ${n.negotiation_number ?? `NEG-${n.id.slice(0, 8)}`}`}
                              className="inline-flex items-center gap-1 rounded-md bg-negotiate px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-negotiate/90"
                            >
                              Open <ArrowRight className="h-3 w-3" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      <SiteFooter />
    </div>
  );
}

/* ─────────────────────── UI bits ─────────────────────── */

function StatCard({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        accent ? "border-negotiate/20 bg-negotiate-soft" : "border-border bg-card"
      }`}
    >
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-display text-2xl text-navy">{value}</div>
    </div>
  );
}

function Avatar({ name, className = "" }: { name: string; className?: string }) {
  const initials = name
    .replace(/[^A-Za-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("") || "??";
  return (
    <span
      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${className}`}
    >
      {initials}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const styles: Record<string, string> = {
    urgent: "bg-red-50 text-red-700 border-red-200",
    high: "bg-orange-50 text-orange-700 border-orange-200",
    normal: "bg-secondary text-navy border-border",
    low: "bg-secondary/60 text-muted-foreground border-border",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
        styles[priority] ?? styles.normal
      }`}
    >
      {priority === "urgent" && <Flame className="h-3 w-3" />}
      {priority}
    </span>
  );
}

function StatusPill({ status }: { status: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-negotiate/20 bg-negotiate-soft px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-negotiate">
      <CircleDot className="h-2.5 w-2.5" />
      {prettyStatus(status)}
    </span>
  );
}

function DaysOpenPill({ days }: { days: number }) {
  const tone =
    days >= 5
      ? "bg-red-50 text-red-700 border-red-200"
      : days >= 2
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-secondary text-navy/70 border-border";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tone}`}
    >
      <Clock className="h-3 w-3" />
      {days}d
    </span>
  );
}

type NextAction =
  | { key: "assign_today"; label: "Assign today"; tone: "red" }
  | { key: "vendor_followup"; label: "Vendor follow-up due"; tone: "amber" }
  | { key: "waiting_customer"; label: "Waiting on customer"; tone: "blue" }
  | { key: "offer_review"; label: "Offer needs review"; tone: "green" }
  | { key: "in_progress"; label: "In progress"; tone: "neutral" }
  | { key: "complete"; label: "Complete"; tone: "emerald" };

function computeNextAction(n: Negotiation, last: NegotiationMessage | null): NextAction {
  if (["completed", "accepted"].includes(n.status)) {
    return { key: "complete", label: "Complete", tone: "emerald" };
  }
  if (n.status === "offer_received") {
    return { key: "offer_review", label: "Offer needs review", tone: "green" };
  }
  if (!n.assigned_specialist_id) {
    return { key: "assign_today", label: "Assign today", tone: "red" };
  }
  if (["vendor_contacted", "waiting_response", "waiting_vendor"].includes(n.status)) {
    const daysSinceMsg = last ? daysBetween(last.created_at) : daysBetween(n.updated_at);
    if (daysSinceMsg >= 2) {
      return { key: "vendor_followup", label: "Vendor follow-up due", tone: "amber" };
    }
  }
  if (last?.sender_role === "customer") {
    return { key: "waiting_customer", label: "Waiting on customer", tone: "blue" };
  }
  return { key: "in_progress", label: "In progress", tone: "neutral" };
}

function NextActionChip({ action }: { action: NextAction }) {
  const toneMap: Record<NextAction["tone"], string> = {
    red: "bg-red-50 text-red-700 border-red-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-green-50 text-green-700 border-green-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    neutral: "bg-secondary text-navy/80 border-border",
  };
  const Icon = action.tone === "red" ? AlertTriangle : CircleDot;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${toneMap[action.tone]}`}
    >
      <Icon className="h-3 w-3" />
      {action.label}
    </span>
  );
}

function EmptyState({ hasAny }: { hasAny: boolean }) {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-negotiate-soft text-negotiate">
        <Handshake className="h-5 w-5" />
      </div>
      <div className="mt-3 font-display text-lg text-navy">
        {hasAny ? "No negotiations match these filters" : "No active negotiations"}
      </div>
      <p className="mt-1.5 text-sm text-muted-foreground">
        {hasAny
          ? "Adjust the filters or clear the search to see more."
          : "This queue lists every Expert Negotiation across the team — new requests appear as soon as a customer purchases one. Assign, message vendors, and settle from here."}
      </p>
      <Link
        to="/operations"
        className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-negotiate hover:underline"
      >
        Back to Operations <ArrowUpRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}

/* ─────────────────────── utils ─────────────────────── */

function prettyStatus(s: string) {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function daysBetween(iso: string) {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

function relativeShort(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function truncate(s: string, n: number) {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
